import React, { useRef } from 'react';
import { useLocation, Location } from 'react-router-dom';
import { AnimatePresence, motion } from 'framer-motion';
import { page, Direction } from '../../motion/presets';

/**
 * ROUTE TRANSITIONS — how one screen gives way to the next.
 *
 * Two layers, so the shell never travels with the page:
 *
 *  `RouteTransition` (outer) wraps the whole `<Routes>`. It is keyed by SHELL
 *  GROUP, not by path: every desk screen (main menu, wizards, parcels, roster,
 *  exit desks, report) shares the key `desk`, so moving between them keeps the
 *  rail, the building panel and the `<main>` mounted and only the content
 *  inside DeskShell transitions. Everything else keys by its first segment.
 *
 *  `ContentTransition` (inner) is what DeskShell, the dashboard and the
 *  account page put around the part that actually changes.
 *
 * Both render `mode="wait"`: the outgoing screen finishes its (instant) exit
 * before the incoming one starts, so two screens never occupy the box at
 * once and the pause reads as a beat rather than a wait.
 *
 * WHY THE RENDER-PROP. The outgoing screen keeps rendering for ~90ms after
 * the URL has changed. If it rendered a bare `<Routes>`, react-router would
 * match the NEW location inside the OLD layer and the leaving screen would
 * flash the arriving one. `<Routes location={location}>` pins the leaving
 * layer to the location it was created with — and react-router also scopes
 * `useLocation()` beneath it to that same location — so the caller must build
 * its `<Routes>` from the location this component hands it.
 */

/* Desk screens share one persistent shell (see layout/desk-shell.tsx). */
const DESK_PATHS = [
  '/main-menu',
  '/visitor-log',
  '/daily-log',
  '/view-report',
  '/roster',
  '/parcels',
  '/visitor-exit',
  '/daily-log-exit',
];

export const isDeskPath = (pathname: string): boolean =>
  DESK_PATHS.some((p) => pathname === p || pathname.startsWith(`${p}/`));

export const segmentOf = (pathname: string): string =>
  `/${pathname.split('/')[1] || ''}`;

export const shellKeyFor = (pathname: string): string =>
  isDeskPath(pathname) ? 'desk' : segmentOf(pathname);

const LOG_SECTIONS = ['/visitor-log', '/daily-log'];

/* Areas reached FROM the main menu — entering slides in from the right (like
   Continue), returning to the menu slides in from the left (like Back). */
const MENU_CHILDREN = [
  '/visitor-log',
  '/daily-log',
  '/view-report',
  '/parcels',
  '/visitor-exit',
  '/daily-log-exit',
  '/roster',
  '/iq-handover',
  '/shift-monitor',
  // Expected today. Direction is resolved per SEGMENT, so '/visitor-log' above
  // already slides it; listed so the menu's children are named in full.
  '/visitor-log/expected',
  '/visitor-log/passes',
];

/** The spatial relationship between two top-level segments. */
export const directionBetween = (prev: string, next: string): Direction => {
  if (prev === next) {
    return 'none';
  }
  // Leaving sign-in: the app rises up out of the login card.
  if (prev === '/') {
    return 'deep';
  }
  if (LOG_SECTIONS.includes(prev) && LOG_SECTIONS.includes(next)) {
    return next === '/daily-log' ? 'down' : 'up';
  }
  if (prev === '/main-menu' && MENU_CHILDREN.includes(next)) {
    return 'forward';
  }
  if (MENU_CHILDREN.includes(prev) && next === '/main-menu') {
    return 'back';
  }

  return 'none';
};

/**
 * Remembers the previous key and resolves the direction of its latest change.
 * The refs only move on a REAL change — a re-render at the same key must not
 * collapse the direction back to 'none' mid-animation.
 */
export const useKeyedDirection = (
  key: string,
  resolve: (prev: string, next: string) => Direction
): Direction => {
  const prevRef = useRef(key);
  const dirRef = useRef<Direction>('none');

  if (key !== prevRef.current) {
    dirRef.current = resolve(prevRef.current, key);
    prevRef.current = key;
  }

  return dirRef.current;
};

interface TransitionLayerProps {
  transitionKey: string;
  direction: Direction;
  children: React.ReactNode;
  /** Animate the first paint too. */
  initial?: boolean;
  className?: string;
  style?: React.CSSProperties;
}

const TransitionLayer: React.FC<TransitionLayerProps> = ({
  transitionKey,
  direction,
  children,
  initial = false,
  className,
  style,
}) => (
  <AnimatePresence mode="wait" initial={initial} custom={direction}>
    <motion.div
      key={transitionKey}
      className={className}
      style={style}
      custom={direction}
      variants={page}
      initial="enter"
      animate="center"
      exit="exit"
      data-route-transition={transitionKey}
    >
      {children}
    </motion.div>
  </AnimatePresence>
);

interface RouteTransitionProps {
  children: (location: Location) => React.ReactNode;
  /** Animate the very first screen in (it arrives under the boot loader). */
  initial?: boolean;
}

export const RouteTransition: React.FC<RouteTransitionProps> = ({
  children,
  initial = true,
}) => {
  const location = useLocation();
  const key = shellKeyFor(location.pathname);
  const segment = segmentOf(location.pathname);

  // Direction is decided from the SEGMENTS either side of a KEY change, so
  // desk→desk moves (same key) leave it alone for the inner layer to handle.
  const prevKeyRef = useRef(key);
  const prevSegmentRef = useRef(segment);
  const dirRef = useRef<Direction>('none');

  if (key !== prevKeyRef.current) {
    dirRef.current = directionBetween(prevSegmentRef.current, segment);
    prevKeyRef.current = key;
  }
  prevSegmentRef.current = segment;

  return (
    <TransitionLayer
      transitionKey={key}
      direction={dirRef.current}
      initial={initial}
    >
      {children(location)}
    </TransitionLayer>
  );
};

interface ContentTransitionProps {
  /** What identifies the content: a segment, a dashboard section, a tab. */
  contentKey: string;
  direction?: Direction;
  children: React.ReactNode;
  className?: string;
  style?: React.CSSProperties;
  initial?: boolean;
}

/**
 * The inner layer: put it around the part of a persistent shell that changes.
 * Pass `useOutlet()`'s element rather than `<Outlet />` — the outlet element
 * captured at render is what keeps the leaving content on its old route.
 */
export const ContentTransition: React.FC<ContentTransitionProps> = ({
  contentKey,
  direction = 'none',
  children,
  className,
  style,
  initial = false,
}) => (
  <TransitionLayer
    transitionKey={contentKey}
    direction={direction}
    className={className}
    style={style}
    initial={initial}
  >
    {children}
  </TransitionLayer>
);
